import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Wallet,
  TrendingUp,
  Target,
  MoreHorizontal,
  X,
  FileText,
  Bell,
  Settings,
} from "lucide-react";
import { useLanguage } from "@/contexts/language-context";

interface BottomNavProps {
  unreadNotifications?: number;
}

type NavKey = "dashboard" | "budget" | "netWorth" | "goals" | "reports" | "notifications" | "settings";

interface NavItem {
  key: NavKey;
  path: string;
  icon: typeof LayoutDashboard;
}

/**
 * Mobile bottom navigation — 5 primary items, safe-area aware.
 * Primary: Dashboard, Budget, Net Worth, Goals, More (sheet).
 * Secondary (in More sheet): Reports, Notifications, Settings.
 */
export function BottomNav({ unreadNotifications = 0 }: BottomNavProps) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);

  const primary: NavItem[] = [
    { key: "dashboard", path: "/", icon: LayoutDashboard },
    { key: "budget", path: "/budget", icon: Wallet },
    { key: "netWorth", path: "/net-worth", icon: TrendingUp },
    { key: "goals", path: "/goals", icon: Target },
  ];

  const secondary: NavItem[] = [
    { key: "reports", path: "/reports", icon: FileText },
    { key: "notifications", path: "/notifications", icon: Bell },
    { key: "settings", path: "/settings", icon: Settings },
  ];

  const isActive = (path: string) =>
    path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

  const go = (path: string) => {
    setMoreOpen(false);
    navigate(path);
  };

  return (
    <nav
      aria-label="Primary"
      className="fixed bottom-0 inset-x-0 z-40 md:hidden border-t border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/75 pb-[env(safe-area-inset-bottom)]"
    >
      <div className="flex items-stretch justify-around">
        {primary.map((item) => {
          const active = isActive(item.path);
          return (
            <button
              key={item.key}
              type="button"
              onClick={() => go(item.path)}
              aria-current={active ? "page" : undefined}
              className="flex flex-1 min-h-[56px] flex-col items-center justify-center gap-1 px-1"
            >
              <span
                className={
                  "flex h-7 w-12 items-center justify-center rounded-full transition-all duration-300 ease-out " +
                  (active ? "bg-primary/15 text-primary scale-105" : "text-muted-foreground scale-100")
                }
              >
                <item.icon className="h-[22px] w-[22px]" strokeWidth={active ? 2.2 : 1.8} />
              </span>
              <span
                className={
                  "text-[10px] font-medium leading-none " +
                  (active ? "text-primary" : "text-muted-foreground")
                }
              >
                {t(`navbar.${item.key}`)}
              </span>
            </button>
          );
        })}

        {/* More */}
        <button
          type="button"
          onClick={() => setMoreOpen(!moreOpen)}
          aria-expanded={moreOpen}
          className="relative flex flex-1 min-h-[56px] flex-col items-center justify-center gap-1 px-1"
        >
          <span
            className={
              "relative flex h-7 w-12 items-center justify-center rounded-full transition-colors " +
              (moreOpen ? "bg-primary/15 text-primary" : "text-muted-foreground")
            }
          >
            {moreOpen ? (
              <X className="h-[22px] w-[22px]" />
            ) : (
              <MoreHorizontal className="h-[22px] w-[22px]" />
            )}
            {unreadNotifications > 0 && (
              <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[9px] font-bold text-destructive-foreground">
                {unreadNotifications > 9 ? "9+" : unreadNotifications}
              </span>
            )}
          </span>
          <span className="text-[10px] font-medium leading-none text-muted-foreground">
            {t("navbar.more")}
          </span>
        </button>
      </div>

      {/* More sheet */}
      {moreOpen && (
        <div className="absolute bottom-full inset-x-0 border-t border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 animate-in slide-in-from-bottom duration-200">
          <div className="grid grid-cols-3 gap-1 px-2 py-3">
            {secondary.map((item) => {
              const active = isActive(item.path);
              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => go(item.path)}
                  aria-current={active ? "page" : undefined}
                  className="flex min-h-[56px] flex-col items-center justify-center gap-1 rounded-lg px-1 hover:bg-muted/60 active:bg-muted"
                >
                  <span className="relative">
                    <item.icon
                      className={
                        "h-5 w-5 " +
                        (active ? "text-primary" : "text-muted-foreground")
                      }
                    />
                    {item.key === "notifications" && unreadNotifications > 0 && (
                      <span className="absolute -top-1 -right-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[9px] font-bold text-destructive-foreground">
                        {unreadNotifications > 9 ? "9+" : unreadNotifications}
                      </span>
                    )}
                  </span>
                  <span
                    className={
                      "text-[10px] font-medium leading-none " +
                      (active ? "text-primary" : "text-muted-foreground")
                    }
                  >
                    {t(`navbar.${item.key}`)}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </nav>
  );
}
